import type { DriveStep } from 'driver.js';

export interface TourDefinition {
  id: string;
  title: string;
  steps: DriveStep[];
}

export interface TourState {
  completedTours: string[];
  skippedTours: string[];
  lastStepIndex: Record<string, number>;
}

export interface TourContextValue {
  activeTourId: string | null;
  isRunning: boolean;
  completedTours: string[];
  startTour: (tourId: string) => void;
  stopTour: () => void;
  skipTour: (tourId: string) => void;
  restartTour: (tourId: string) => void;
  resetAllTours: () => void;
  isTourCompleted: (tourId: string) => boolean;
  shouldAutoStart: (tourId: string) => boolean;
  getCurrentTourId: () => string | undefined;
}

export const TOUR_IDS = {
  GLOBAL: 'global',
  DASHBOARD: 'dashboard',
  EMAIL: 'email',
  LEADS: 'leads',
  COMPANIES: 'companies',
  DEALS: 'deals',
  ACTIVITIES: 'activities',
  CHAT: 'chat',
  LEAD_PROFILE: 'lead-profile',
  DEAL_PROFILE: 'deal-profile',
  COMPANY_PROFILE: 'company-profile',
} as const;

export type TourId = (typeof TOUR_IDS)[keyof typeof TOUR_IDS];

// Exact path matches
const EXACT_PATH_TO_TOUR: Record<string, TourId> = {
  '/dashboard': TOUR_IDS.DASHBOARD,
  '/email': TOUR_IDS.EMAIL,
  '/leads': TOUR_IDS.LEADS,
  '/companies': TOUR_IDS.COMPANIES,
  '/deals': TOUR_IDS.DEALS,
  '/deals/kanban': TOUR_IDS.DEALS,
  '/deals/list': TOUR_IDS.DEALS,
  '/deals/forecast': TOUR_IDS.DEALS,
  '/activities': TOUR_IDS.ACTIVITIES,
  '/chat': TOUR_IDS.CHAT,
};

// Pattern matches for dynamic routes (order matters - more specific first)
const PATTERN_PATH_TO_TOUR: Array<{ pattern: RegExp; tourId: TourId }> = [
  { pattern: /^\/leads\/[^/]+$/, tourId: TOUR_IDS.LEAD_PROFILE },
  { pattern: /^\/deals\/[^/]+$/, tourId: TOUR_IDS.DEAL_PROFILE },
  { pattern: /^\/companies\/[^/]+$/, tourId: TOUR_IDS.COMPANY_PROFILE },
];

// Paths to exclude from pattern matching (edit pages, create pages, etc.)
const EXCLUDED_PATHS = [
  '/leads/new', '/leads/import',
  '/deals/new', '/deals/kanban', '/deals/list', '/deals/forecast',
  '/companies/new',
];

export function getTourIdForPath(pathname: string): TourId | undefined {
  // Check exact matches first
  const exact = EXACT_PATH_TO_TOUR[pathname];
  if (exact) return exact;

  // Exclude known non-profile paths
  if (EXCLUDED_PATHS.includes(pathname)) return undefined;

  // Check pattern matches for dynamic routes
  for (const { pattern, tourId } of PATTERN_PATH_TO_TOUR) {
    if (pattern.test(pathname)) return tourId;
  }

  return undefined;
}
